import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Avatar, Menu, MenuItem, Divider, Tooltip,
} from "@mui/material";
import {
  ChevronDown, UserPlus, MessageCircle, PanelLeft, User, LogOut,
} from "lucide-react";
import { clearLightcastToken, logout } from "../../redux/authSlice";
import { clearUser } from "../../redux/userSlice";
import { persistor } from "../../redux/store";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { openChat, setChatType } from "../../redux/chatSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Header = ({ toggleSidebar, sidebarOpen, toggleMobileSidebar, mobileSidebarOpen }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const profilePhoto = useSelector((state) => state.user.profilePhoto);
  const mentorProfileInfo = useSelector((state) => state.mentor);
  const { type, subscription, isProfileComplete } = useSelector((state) => state.auth);
  const currentJob = useSelector((state) => state.user.currentJob);
  const targetJob = useSelector((state) => state.user.targetJob);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isAdmin = type === "admin" || localStorage.getItem("userType") === "admin";
  const isMentor = type === "mentor" || localStorage.getItem("userType") === "mentor";

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleProfile = () => {
    navigate(type === "user" ? "/dashboard/profile" : "/dashboard/profile-settings");
  };

  const handleLogout = async () => {
    try {
      await api1.post(API_ENDPOINTS.LOGOUT, {
        refreshToken: localStorage.getItem("refreshToken"),
      });

      localStorage.clear();
      persistor.purge();
      dispatch(clearUser());
      dispatch(logout());
      dispatch(clearLightcastToken());

      const domain = window.location.hostname;
      const isLocalhost = ['localhost', '127.0.0.1'].includes(domain);
      const isIPAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(domain);
      const rootDomain = isIPAddress ? null : domain.split('.').slice(-2).join('.');

      const clearCookie = (name) => {
        const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
        const base = `${name}=;${expires};path=/`;

        document.cookie = base;
        document.cookie = `${base};domain=${domain}`;
        if (rootDomain) {
          document.cookie = `${base};domain=.${rootDomain}`;
        }
        if (isLocalhost) {
          document.cookie = `${base};domain=localhost`;
          document.cookie = `${base};domain=127.0.0.1`;
        }
      };

      document.cookie.split(";").forEach(cookie => {
        const name = cookie.split("=")[0].trim();
        if (name) clearCookie(name);
      });

      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      navigate("/auth/login");

    } catch (error) {
      console.error("Error during logout:", error.message);
    }
  };

  const handleChat = (chatType = "general") => {
    if (!["onetime", "pro-monthly", "pro-yearly"].includes(subscription?.plan)) {
      toast.error("Upgrade plan to access");
      return;
    }

    dispatch(openChat());
    dispatch(setChatType(chatType));
  };

  const handleSidebarToggle = () => {
    windowWidth < 768 ? toggleMobileSidebar() : toggleSidebar();
  };

  const avatarSrc = localStorage.getItem("userType") === "user" ? profilePhoto : mentorProfileInfo?.profileImage;

  return (
    <div className="shadow-lg border-b border-gray-200 relative z-40">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 mb-2 mt-2 lg:px-6">
        <div className="flex items-center justify-between h-12 sm:h-14 lg:h-16">
          <div className="flex items-center">
            <button
              onClick={handleSidebarToggle}
              className="text-gray-600 hover:text-gray-900 p-1.5 sm:p-2 rounded-md hover:bg-gray-100 transition-colors"
              title={
                windowWidth < 768
                  ? mobileSidebarOpen ? "Hide Sidebar" : "Show Sidebar"
                  : sidebarOpen ? "Hide Sidebar" : "Show Sidebar"
              }
            >
              <PanelLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {!isAdmin && !isMentor && (
            <div className="flex-1 flex justify-start items-center px-1 sm:px-2">
              <div className="hidden sm:flex items-center text-xs lg:text-sm w-full">
                <div className="flex items-center gap-1 lg:gap-2">
                  <span className="text-[#6e28cc] font-medium">Current Occupation:</span>
                  <span className="text-gray-900 font-semibold">{currentJob || "Not set"}</span>
                </div>
                <div className="flex items-center gap-1 lg:gap-2 ml-4">
                  <span className="text-[#6e28cc] font-medium">Target Occupation:</span>
                  <span className="text-gray-900 font-semibold">{targetJob || "Not set"}</span>
                </div>
              </div>

              <div className="sm:hidden flex flex-col items-center text-[10px] w-full">
                <div className="flex items-center gap-1 justify-center">
                  <span className="text-[#6e28cc] font-medium">Current:</span>
                  <span className="text-gray-900 font-semibold">{currentJob || "Not set"}</span>
                </div>
                <div className="flex items-center gap-1 justify-center">
                  <span className="text-[#6e28cc] font-medium">Target:</span>
                  <span className="text-gray-900 font-semibold">{targetJob || "Not set"}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-1 sm:gap-2 lg:gap-3">
            {!isAdmin && !isMentor && (
              <>
                <Tooltip title={!isProfileComplete ? "Please complete your profile first" : ""} arrow>
                  <span>
                    <button
                      onClick={() => isProfileComplete && navigate("/dashboard/mentors-and-community/mentorship")}
                      disabled={!isProfileComplete}
                      className={`hidden sm:flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1.5 lg:py-2 border border-[#6e28cc] text-[#6e28cc] bg-white rounded-lg transition-colors text-xs lg:text-sm font-medium ${
                        !isProfileComplete 
                          ? "opacity-50 cursor-not-allowed" 
                          : "hover:bg-purple-50"
                      }`}
                    >
                      <UserPlus className="w-3 h-3 lg:w-4 lg:h-4" />
                      <span className="hidden lg:inline">Connect with a Mentor</span>
                      <span className="lg:hidden">Mentor</span>
                    </button>
                  </span>
                </Tooltip>

                <Tooltip title={!isProfileComplete ? "Please complete your profile first" : ""} arrow>
                  <span>
                    <button
                      onClick={() => isProfileComplete && navigate("/dashboard/mentors-and-community/community")}
                      disabled={!isProfileComplete}
                      className={`hidden sm:flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1.5 lg:py-2 border border-[#6e28cc] text-[#6e28cc] bg-white rounded-lg transition-colors text-xs lg:text-sm font-medium ${
                        !isProfileComplete 
                          ? "opacity-50 cursor-not-allowed" 
                          : "hover:bg-purple-50"
                      }`}
                    >
                      <MessageCircle className="w-3 h-3 lg:w-4 lg:h-4" />
                      <span className="hidden lg:inline">Join a Community</span>
                      <span className="lg:hidden">Community</span>
                    </button>
                  </span>
                </Tooltip>

                <div
                  className="flex items-center space-x-1 sm:space-x-2 border border-gray-300 rounded-full cursor-pointer p-0.5 sm:p-1 hover:bg-gray-50 transition-colors"
                  onClick={handleClick}
                >
                  <Avatar
                    alt="Profile Picture"
                    src={avatarSrc}
                    className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8"
                  />
                  <ChevronDown className="text-gray-500 w-3 h-3 sm:w-4 sm:h-4" />
                </div>
              </>
            )}

            {isMentor && (
              <div
                className="flex items-center space-x-1 sm:space-x-2 border border-gray-300 rounded-full cursor-pointer p-0.5 sm:p-1 hover:bg-gray-50 transition-colors"
                onClick={handleClick}
              >
                <Avatar
                  alt="Profile Picture"
                  src={avatarSrc}
                  className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8"
                />
                <ChevronDown className="text-gray-500 w-3 h-3 sm:w-4 sm:h-4" />
              </div>
            )}

            {isAdmin && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1.5 lg:py-2 border border-red-500 text-red-500 bg-white rounded-lg hover:bg-red-50 transition-colors text-xs lg:text-sm font-medium"
              >
                <LogOut className="w-3 h-3 lg:w-4 lg:h-4" />
                <span className="hidden lg:inline">Logout</span>
                <span className="lg:hidden">Logout</span>
              </button>
            )}
          </div>
        </div>

        {!isAdmin && (
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              elevation: 8,
              sx: {
                mt: 1,
                borderRadius: 2,
                minWidth: { xs: 180, sm: 200, md: 220 },
                maxWidth: { xs: "85vw", sm: 300, md: 320 },
              },
            }}
          >
            {!isMentor && windowWidth < 640 && (
              <>
                <MenuItem
                  onClick={() => {
                    if (isProfileComplete) {
                      navigate("/dashboard/mentors-and-community/mentorship");
                      handleClose();
                    }
                  }}
                  disabled={!isProfileComplete}
                  sx={{
                    opacity: !isProfileComplete ? 0.5 : 1,
                    cursor: !isProfileComplete ? 'not-allowed' : 'pointer'
                  }}
                >
                  <UserPlus className={`w-4 h-4 ${!isProfileComplete ? 'text-gray-400' : 'text-[#6e28cc]'}`} />
                  <span className={`text-xs sm:text-sm font-medium ml-2 ${!isProfileComplete ? 'text-gray-400' : ''}`}>
                    Connect with a Mentor
                  </span>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    if (isProfileComplete) {
                      navigate("/dashboard/mentors-and-community/community");
                      handleClose();
                    }
                  }}
                  disabled={!isProfileComplete}
                  sx={{
                    opacity: !isProfileComplete ? 0.5 : 1,
                    cursor: !isProfileComplete ? 'not-allowed' : 'pointer'
                  }}
                >
                  <MessageCircle className={`w-4 h-4 ${!isProfileComplete ? 'text-gray-400' : 'text-[#6e28cc]'}`} />
                  <span className={`text-xs sm:text-sm font-medium ml-2 ${!isProfileComplete ? 'text-gray-400' : ''}`}>
                    Join a Community
                  </span>
                </MenuItem>
                <Divider />
              </>
            )}

            <MenuItem
              onClick={() => {
                handleProfile();
                handleClose();
              }}
            >
              <User className="w-4 h-4 text-gray-600" />
              <span className="text-xs sm:text-sm font-medium ml-2">Profile</span>
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={() => {
                handleLogout();
                handleClose();
              }}
            >
              <LogOut className="w-4 h-4 text-red-600" />
              <span className="text-xs sm:text-sm font-medium text-red-600 ml-2">Logout</span>
            </MenuItem>
          </Menu>
        )}
      </div>
    </div>
  );
};

export default Header;